import React, { Dispatch, SetStateAction, useState } from "react"
import FileDownload from "js-file-download"
import { useHistory } from "react-router-dom"
import {
	Button,
	CircularProgress,
	Grid
} from "@material-ui/core"
import { ClientCatalogPagination } from "@/protocols/clientCatalog"
import ExportContactsDialog from "@/pages/Admin/ClientCatalog/ExportContactsDialog"
import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import AddClientDialog from "@/components/AddClientDialog"
import ContactGroupPermissionGate from "@/components/PermissionGate/ContactGroupPermissionGate"
import CustomFieldsDialog from "@/pages/Admin/CustomFields"
import { ClientCatalogWhereData } from "@/pages/Admin/ClientCatalog"
import { Add } from "@material-ui/icons"
import { ListPlus as ListPlusIcon } from "lucide-react"
import useStyles from "@/pages/Admin/ClientCatalog/ClientManageContactsHeader/styles"
import { ReactComponent as DownloadFileIcon } from "@/assets/icons/donwload_file_icon.svg"
import { ReactComponent as UploadFileIcon } from "@/assets/icons/upload_file_icon.svg"
import useCustomStyles from "@/styles/custom"
import { BaseContactsFiltersType } from "@/protocols/ClientCatalogFilterProtocol"

type CatalogHeaderProps = {
	setClientCatalog: Dispatch<SetStateAction<ClientCatalogPagination>>
	selectedContactsFilter?: BaseContactsFiltersType
}

const ClientManageContactsHeader: React.FC<CatalogHeaderProps> = ({ selectedContactsFilter, setClientCatalog }) => {
	const [exportingContacts, setExportingClients] = useState(false)
	const [whereData, setWhereData] = useState<ClientCatalogWhereData>({
		page: 0,
		rowsPerPage: 20
	})
	const { triggerValidation } = useValidation()
	const history = useHistory()

	const isSelectedContactsFilterNotEmpty = selectedContactsFilter && Object.keys(selectedContactsFilter).length > 0

	const getAndUpdateClientCatalogData = async (newWhereData?: Partial<ClientCatalogWhereData>) => {
		try {
			const whereParams = newWhereData

			setWhereData({
				...whereData,
				...whereParams
			})

			const { data } = await ApiService.get("/clients", {
				params: {
					...whereData,
					...whereParams,
					contactType: "user"
				}
			})
			setClientCatalog({
				clientCatalog: data.clientCatalog,
				count: data.count,
				rows: data.rows
			})
		} catch (error) {
			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const handleExportClients = async () => {
		ExportContactsDialog.open({
			onConfirm: async () => {
				setExportingClients(true)
				let newWhereData: Partial<ClientCatalogWhereData> = {}

				if (isSelectedContactsFilterNotEmpty) {
					newWhereData = {
						contactsFilters: selectedContactsFilter
					}
				}

				try {
					const response = await ApiService.get("/clients/exportation/export", {
						responseType: "blob",
						params: {
							...whereData,
							...newWhereData
						}
					})
					FileDownload(response.data, "contatos.csv")
				} catch (error) {
					ErrorHandlerService.handle(error as ErrorType)
				}
				setExportingClients(false)
			}
		})
	}
	const classes = useStyles()
	const customClasses = useCustomStyles()

	const linkToContactImportPage = "/admin/contacts/import-contacts"

	return (
		<Grid container spacing={2}>
			<Grid item>
				<ContactGroupPermissionGate action="create">
					<AddClientDialog
						nickname={whereData.search}
						onAdd={getAndUpdateClientCatalogData}
					>
						<Button
							variant="contained"
							className={customClasses.primaryActionButton}
							startIcon={<Add />}
						>
							Adicionar contato
						</Button>
					</AddClientDialog>
				</ContactGroupPermissionGate>
			</Grid>
			<Grid item>
				<ContactGroupPermissionGate action="export">
					<Button
						variant="contained"
						className={classes.buttonPrimaryColorOutLined}
						onClick={handleExportClients}
						startIcon={<DownloadFileIcon/>}
						endIcon={exportingContacts && <CircularProgress size={20} />}
					>
						Exportar contatos
					</Button>
				</ContactGroupPermissionGate>
			</Grid>

			<Grid item>
				<ContactGroupPermissionGate action="create">
					<Button
						variant="contained"
						className={classes.buttonPrimaryColorOutLined}
						startIcon={<UploadFileIcon/>}
						onClick={() => history.push(linkToContactImportPage)}
					>
						Importar contatos
					</Button>
				</ContactGroupPermissionGate>
			</Grid>

			<Grid item>
				<CustomFieldsDialog
				>
					<Button
						variant="contained"
						className={classes.buttonPrimaryColorOutLined}
						startIcon={<ListPlusIcon size={20} />}
					>
						Campos Personalizados
					</Button>
				</CustomFieldsDialog>
			</Grid>
		</Grid>
	)
}

export default ClientManageContactsHeader
